import { authenticatedApi } from '../../../shared/api/apiClientUtils.ts';
import { unwrap } from '@billda/api';
import type {
  LabourItemPreview,
  PlotPreview,
  SiteImportPreview,
  SiteImportRequest,
  SiteImportResponse,
  UpdateSiteRequest,
  UpdateSiteResponse,
} from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import { SitesSummaryResponse } from '../types/sitesTypes.ts';
import type { PlotEventsTimelineResponse } from '../types/plotTimeline';
import type { SiteEventsTimelineResponse } from '../types/siteTimeline';
import type { LabourItemEventsTimelineResponse } from '../types/labourItemTimeline';
import type { AllocationEventsTimelineResponse } from '../types/allocationTimeline';
import type { VariationEventsTimelineResponse } from '../types/variationTimeline';
import type {
  ReimportPreviewResponse,
  ReimportExecuteResponse,
} from '../types/reimportTypes';

export type {
  LabourItemPreview,
  PlotPreview,
  SiteImportRequest,
  SiteImportResponse,
};
export type SiteImportPreviewResponse = SiteImportPreview;

export interface CheckSlugUniquenessResponse {
  isUnique: boolean;
  errorMessage: string | null;
}

export interface ValidateWhat3WordsResponse {
  isValid: boolean;
  latitude: number | null;
  longitude: number | null;
  errorMessage: string | null;
}

// Plot status
export type PlotStatus = 'NotStarted' | 'InProgress' | 'Complete';

// Labour Item status (shared with allocations)
export type LabourItemStatus = 'NotStarted' | 'InProgress' | 'Complete';

// Allocation interfaces for labour item allocations
export type AllocationStatus =
  | 'InProgress'
  | 'Booked'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled';

export const previewSiteImport = async (
  file: File
): Promise<SiteImportPreviewResponse> => {
  return unwrap(
    await api.legacy.POST('/api/v1/sites/import/preview', {
      body: { csvFile: file as unknown as string },
      bodySerializer: body => {
        const form = new FormData();
        form.append('csvFile', body.csvFile);
        return form;
      },
    })
  );
};

export const importSite = async (
  request: SiteImportRequest
): Promise<SiteImportResponse> => {
  return unwrap(
    await api.legacy.POST('/api/v1/sites/import', { body: request })
  );
};

export const checkSlugUniqueness = async (
  slug: string
): Promise<CheckSlugUniquenessResponse> => {
  const response = await authenticatedApi.get<CheckSlugUniquenessResponse>(
    `/api/v1/sites/check-slug?slug=${encodeURIComponent(slug.toUpperCase())}`
  );

  return response.data;
};

export const validateWhat3Words = async (
  words: string
): Promise<ValidateWhat3WordsResponse> => {
  const response = await authenticatedApi.get<ValidateWhat3WordsResponse>(
    `/api/v1/sites/validate-what3words?words=${encodeURIComponent(words)}`
  );

  return response.data;
};

export const getSitesSummary = async (
  search?: string
): Promise<SitesSummaryResponse> => {
  const queryParams = new URLSearchParams();
  if (search) queryParams.append('search', search);

  const queryString = queryParams.toString();
  const url = queryString
    ? `/api/v1/sites/summary?${queryString}`
    : '/api/v1/sites/summary';

  const response = await authenticatedApi.get<SitesSummaryResponse>(url);

  return response.data;
};

// Variation Summary interfaces
export type VariationRequestStatus = 'Pending' | 'Approved' | 'Rejected';

export interface VariationSummaryDto {
  id: number;
  title: string;
  description?: string;
  notes?: string;
  requestedBy: string;
  requestedDate: string;
  status: VariationRequestStatus;
  approvedDate?: string;
  approvedBy?: string;
  financialSummary?: {
    requestedAmount: number;
    approvedAmount?: number;
  };
}

export const getVariationSummary = async (
  variationId: string
): Promise<VariationSummaryDto> => {
  const response = await authenticatedApi.get<{
    variation: VariationSummaryDto;
  }>(`/api/v1/variations/${variationId}/summary`);

  return response.data.variation;
};

/**
 * Get paginated plot events timeline
 */
export const getPlotEventsTimeline = async (
  siteId: number,
  plotId: number,
  params?: {
    skip?: number;
    take?: number;
  }
): Promise<PlotEventsTimelineResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const queryString = queryParams.toString();
  const url = queryString
    ? `/api/v1/sites/${siteId}/plots/${plotId}/events?${queryString}`
    : `/api/v1/sites/${siteId}/plots/${plotId}/events`;

  const response = await authenticatedApi.get<PlotEventsTimelineResponse>(url);

  return response.data;
};

/**
 * Get paginated site events timeline
 */
export const getSiteEventsTimeline = async (
  siteId: number,
  params?: {
    skip?: number;
    take?: number;
  }
): Promise<SiteEventsTimelineResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const queryString = queryParams.toString();
  const url = queryString
    ? `/api/v1/sites/${siteId}/events?${queryString}`
    : `/api/v1/sites/${siteId}/events`;

  const response = await authenticatedApi.get<SiteEventsTimelineResponse>(url);

  return response.data;
};

/**
 * Get paginated labour item events timeline
 */
export const getLabourItemEventsTimeline = async (
  siteId: number,
  plotId: number,
  labourItemId: number,
  params?: {
    skip?: number;
    take?: number;
  }
): Promise<LabourItemEventsTimelineResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const queryString = queryParams.toString();
  const url = queryString
    ? `/api/v1/sites/${siteId}/plots/${plotId}/labour-items/${labourItemId}/events?${queryString}`
    : `/api/v1/sites/${siteId}/plots/${plotId}/labour-items/${labourItemId}/events`;

  const response =
    await authenticatedApi.get<LabourItemEventsTimelineResponse>(url);

  return response.data;
};

/**
 * Get paginated allocation events timeline
 */
export const getAllocationEventsTimeline = async (
  siteId: number,
  plotId: number,
  allocationId: string,
  params?: {
    skip?: number;
    take?: number;
  }
): Promise<AllocationEventsTimelineResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const queryString = queryParams.toString();
  const url = queryString
    ? `/api/v1/sites/${siteId}/plots/${plotId}/allocations/${allocationId}/events?${queryString}`
    : `/api/v1/sites/${siteId}/plots/${plotId}/allocations/${allocationId}/events`;

  const response =
    await authenticatedApi.get<AllocationEventsTimelineResponse>(url);

  return response.data;
};

/**
 * Get paginated variation events timeline
 */
export const getVariationEventsTimeline = async (
  siteId: number,
  plotId: number,
  variationId: string,
  params?: {
    skip?: number;
    take?: number;
  }
): Promise<VariationEventsTimelineResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const queryString = queryParams.toString();
  const url = queryString
    ? `/api/v1/sites/${siteId}/plots/${plotId}/variations/${variationId}/events?${queryString}`
    : `/api/v1/sites/${siteId}/plots/${plotId}/variations/${variationId}/events`;

  const response =
    await authenticatedApi.get<VariationEventsTimelineResponse>(url);

  return response.data;
};

// Create Plot interfaces
export interface CreatePlotRequest {
  name: string;
  plotNumber: string;
}

export interface CreatePlotResponse {
  plotId: number;
  name: string;
  plotNumber: string;
  siteId: number;
}

/**
 * Export site data as CSV
 */
export const exportSiteCsv = async (siteId: number): Promise<void> => {
  const response = await authenticatedApi.get(
    `/api/v1/sites/${siteId}/export`,
    { responseType: 'blob' }
  );

  const contentDisposition = response.headers['content-disposition'];
  let filename = `site-${siteId}-export.csv`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match?.[1]) {
      filename = match[1];
    }
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const previewSiteReimport = async (
  siteId: number,
  file: File
): Promise<ReimportPreviewResponse> => {
  return unwrap(
    await api.legacy.POST('/api/v1/sites/{siteId}/reimport/preview', {
      params: { path: { siteId } },
      body: { csvFile: file as unknown as string },
      bodySerializer: body => {
        const form = new FormData();
        form.append('csvFile', body.csvFile);
        return form;
      },
    })
  );
};

export const executeSiteReimport = async (
  siteId: number,
  file: File
): Promise<ReimportExecuteResponse> => {
  return unwrap(
    await api.legacy.POST('/api/v1/sites/{siteId}/reimport', {
      params: { path: { siteId } },
      body: { csvFile: file as unknown as string },
      bodySerializer: body => {
        const form = new FormData();
        form.append('csvFile', body.csvFile);
        return form;
      },
    })
  );
};

export interface BackfillCoordinatesResponse {
  latitude: number;
  longitude: number;
}

export const backfillSiteCoordinates = async (
  siteId: number
): Promise<BackfillCoordinatesResponse> => {
  const response = await authenticatedApi.post<BackfillCoordinatesResponse>(
    `/api/v1/sites/${siteId}/backfill-coordinates`
  );

  return response.data;
};

export const updateSite = async (
  siteId: number,
  body: UpdateSiteRequest
): Promise<UpdateSiteResponse> => {
  return unwrap(
    await api.legacy.PATCH('/api/v1/sites/{siteId}', {
      params: { path: { siteId } },
      body,
    })
  );
};

export const archiveSite = async (siteId: number) => {
  return unwrap(
    await api.legacy.POST('/api/v1/sites/{siteId}/archive', {
      params: { path: { siteId } },
    })
  );
};

export const createPlot = async (
  siteId: number,
  request: CreatePlotRequest
): Promise<CreatePlotResponse> => {
  const response = await authenticatedApi.post<CreatePlotResponse>(
    `/api/v1/sites/${siteId}/plots`,
    request,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

export interface BurndownDataPoint {
  date: string;
  paid: number;
  inProgress: number;
  unbooked: number;
  variations: number;
}

export interface GetSiteBurndownResponse {
  dataPoints: BurndownDataPoint[];
  totalLabourValue: number;
}

export const getSiteBurndown = async (
  siteId: number
): Promise<GetSiteBurndownResponse> => {
  const response = await authenticatedApi.get<GetSiteBurndownResponse>(
    `/api/v1/sites/${siteId}/burndown`
  );
  return response.data;
};
